/**
 * SUPERVISOR.ACCOUNT
 * Edit account options
 */
import { useEffect, useState } from "react"
import { connect } from "react-redux"
import { WithTranslation, withTranslation } from "react-i18next"
import { AccountState } from "@/redux/account.types"
import NavFocus from "@/nav/nav.focus"
import PageHeader from "@/components/page-header"
import Button from "@/components/button"
import Modal from "@/components/modal"
import TextInput from "@/components/text-input"
import { store } from "@/index"
import { accountEdit, accountStatus, accountUpdate_AsSupervisor } from "@/redux/account.actions"
import { updateAccountSector_AsSupervisor } from "@/features/benchmark/entities/question/model/benchmarkQuestion.thunk"
import { STATUS_SAVED, STATUS_SAVE_ERROR, STATUS_SAVING } from "@/redux/_status.types"
import Checkbox from "@/components/checkbox"
import ListItem from "@/components/list-item"
import ListCell from "@/components/list-cell"
import Space from "@/components/space"
import DeleteAccountModal from "@/modals/delete-account.modal"
import LoginModal from "@/modals/login.modal"
import { Session } from "@/redux/_session.types"
import { faSearch, faSkull } from "@fortawesome/free-solid-svg-icons"
import ModulesWidget from "@/widgets/modules.widget"
import ModalConfirm from "@/components/modal-confirm"
import Dropdown from "@/components/dropdown"
import AccountSubscriptionsWidget from "@/widgets/account-subscriptions.widget"
import { NavigateFunction, useNavigate } from "react-router-dom"
import { supervisorFetch, supervisorGet } from "@/redux/supervisor.actions"
import Link from "@/components/link"
import ConvertToSuperaccountModal from "@/modals/convert-to-superaccount.modal"
import { Supervisor } from "@/redux/supervisor.types"
import { surveyImportDemo_AsSupervisor } from "@/redux/survey.actions"
import EnterCodeWidget from "@/widgets/enter-code.widget"
import { toast } from "react-toastify"
import { BounceIn } from "@/utils/animations.utils"
import AccountEditCsWidget from "@/widgets/account-edit-cs.widget"
import { BENCHMARK_SECTOR_LIST } from "@/redux/benchmark.types"
import ListButton from "@/components/list-button"
import AccountSearchParentModal from "@/modals/account-search-parent.modal"

interface Props extends WithTranslation {
  _session: Session
  account: AccountState
}

//Modal
const MODAL_CONVERT: string = "MODAL_CONVERT" //Convertir
const MODAL_LOGIN: string = "MODAL_LOGIN" //Loading before connect to account
const MODAL_DELETE_CONFIRM: string = "MODAL_DELETE_CONFIRM" //Confirm delete
const MODAL_SUPERACCOUNT: string = "MODAL_SUPERACCOUNT" //Warning when change superaccount
const MODAL_IMPORT_DEMO_SURVEY: string = "MODAL_IMPORT_DEMO_SURVEY" //Warning demo survey generation
const MODAL_SEARCH_PARENT = "MODAL_SEARCH_PARENT"

function SuperTrials(props: Props) {
  const { t } = props
  const navigate: NavigateFunction = useNavigate()

  const queryParams: URLSearchParams = new URLSearchParams(window.location.search)
  const tab: string | null = queryParams.get("tab") //Tab of the accounts page to go back to

  const [currentModal, setCurrentModal] = useState<string | null>(null)
  const [isImportingDemoSurvey, setIsImportingDemoSurvey] = useState<boolean>(false)
  const [withDemoSurveyHistory, setWithDemoSurveyHistory] = useState<boolean>(false)
  const [supervisors, setSupervisors] = useState<Supervisor[]>([])

  //Load supervisors list
  //Set supervisor and supervisor sales
  useEffect(() => {
    async function loadSupervisors() {
      const response: any = await store.dispatch(supervisorFetch(""))
      if (response.error) {
        store.dispatch(supervisorGet(response.error))
      } else {
        setSupervisors(response.map((x) => new Supervisor(x)))
        const supervisor = response.find((x) => x.id === props.account.active.SupervisorId)
        const supervisorSales = response.find((x) => x.id === props.account.active.SupervisorIdSales)

        if (supervisor) {
          store.dispatch(accountEdit("Supervisor", new Supervisor(supervisor)))
        }

        if (supervisorSales) {
          store.dispatch(accountEdit("SupervisorSales", new Supervisor(supervisorSales)))
        }
      }
    }

    loadSupervisors()
  }, [])

  //Delete account
  //Open confirmation modal
  function deleteAccount() {
    setCurrentModal(MODAL_DELETE_CONFIRM)
  }

  //Generate the RPS and QVT demonstration surveys
  async function importDemoSurvey() {
    setIsImportingDemoSurvey(true)
    const response: any = await store.dispatch(
      surveyImportDemo_AsSupervisor(props.account.active.id, withDemoSurveyHistory)
    )
    setIsImportingDemoSurvey(false)
    setCurrentModal(null)

    if (response?.error) return

    const removed = response.removedSurveys?.length
    const currentSurveys = response.currentSurveys ?? [response]
    const currentIds = new Set(currentSurveys.map((survey: any) => survey.surveyId))
    const history = response.surveys?.filter((survey: any) => !currentIds.has(survey.surveyId)) ?? []
    toast(
      currentSurveys
        .map(
          (survey: any) =>
            `Enquête « ${survey.name} » créée : ${survey.respondents} répondants sur ${survey.participants} invités, ${survey.messages} commentaires.`
        )
        .join(" ") +
        (history.length ? ` Historique : ${history.map((survey: any) => survey.name).join(", ")}.` : "") +
        (removed ? ` ${removed} enquête(s) de démonstration remplacée(s).` : ""),
      { type: "success" }
    )
  }

  //Connect to account
  function getAccount() {
    setCurrentModal(MODAL_LOGIN)
  }

  //Get supervisor
  function getSupervisor(isSales: boolean) {
    return isSales ? props.account.active.SupervisorSales : props.account.active.Supervisor
  }

  //Edit informations on account
  function handleAccountChanges(event: any) {
    store.dispatch(accountEdit(event.id, event.value))
  }

  //Change checkbox value
  function handleAccountCheckbox(event: any) {
    if (event.value === false && event.id === "isSuperAccount") {
      setCurrentModal(MODAL_SUPERACCOUNT)
    } else {
      handleAccountChanges(event)
      saveAccount()
    }
  }

  //Change options
  function handleAccountOptionsChanges(key, value) {
    const options = Object.assign({}, props.account.active.options, { [key]: value })
    handleAccountChanges({ id: "options", value: options })
  }

  //Remove superaccount property
  function removeSuperAccount() {
    setCurrentModal(null)
    handleAccountChanges({ id: "isSuperAccount", value: false })
    saveAccount()
  }

  //Save account
  async function saveAccount() {
    store.dispatch(accountStatus(STATUS_SAVING))
    const response: any = await store.dispatch(accountUpdate_AsSupervisor(props.account.active))
    store.dispatch(accountStatus(response.error ? STATUS_SAVE_ERROR : STATUS_SAVED))
  }

  //Select supervisor and edit account
  function selectSupervisor(supervisor: Supervisor | null, isSales) {
    if (isSales) {
      store.dispatch(accountEdit("SupervisorSales", supervisor))
      store.dispatch(accountEdit("SupervisorIdSales", supervisor ? supervisor.id : null))
    } else {
      store.dispatch(accountEdit("Supervisor", supervisor))
      store.dispatch(accountEdit("SupervisorId", supervisor ? supervisor.id : null))
    }
  }

  return (
    <NavFocus
      text={t("accounts")}
      onBack={() => navigate("/supervisor/accounts" + (tab ? "?tab=" + tab : ""))}
      status={props.account.status}>
      {currentModal === MODAL_CONVERT && <ConvertToSuperaccountModal onClose={() => setCurrentModal(null)} />}
      {currentModal === MODAL_SEARCH_PARENT && (
        <AccountSearchParentModal
          onClick={(accountId) => {
            handleAccountChanges({ id: "superAccountId", value: accountId })
            setTimeout(() => {
              saveAccount()
            }, 250)
          }}
          onClose={() => setCurrentModal(null)}
        />
      )}

      {
        /** dialog to connect to login page */
        currentModal === MODAL_LOGIN && (
          <LoginModal
            authLevel={2}
            isSupervisor
            accountIdForSuperadmin={props.account.active.id}
            email={props._session.email}
          />
        )
      }

      {
        /** warning when change superaccount */
        currentModal === MODAL_SUPERACCOUNT && (
          <ModalConfirm
            onNo={() => setCurrentModal(null)}
            onYes={removeSuperAccount}
            text={t("account_remove_superaccount")}
            textBold={t("utils_next_ask")}
          />
        )
      }

      {
        /** dialog to delete account */
        currentModal === MODAL_DELETE_CONFIRM && (
          <DeleteAccountModal
            onClose={() => setCurrentModal(null)}
            accountId={props.account.active.id}
            accountName={props.account.active.name}
          />
        )
      }

      {currentModal === MODAL_IMPORT_DEMO_SURVEY && (
        <Modal title="Enquêtes de démonstration" onClose={() => setCurrentModal(null)}>
          <p>
            Deux enquêtes de démonstration, RPS et QVT, seront créées sur le compte « {props.account.active.name} ».
          </p>
          <Checkbox
            active={withDemoSurveyHistory}
            text="Importer l'historique des enquêtes RPS"
            onClick={() => setWithDemoSurveyHistory(!withDemoSurveyHistory)}
          />
          <EnterCodeWidget
            labelCta="Générer"
            onCancel={() => setCurrentModal(null)}
            onNext={importDemoSurvey}
            status={isImportingDemoSurvey ? STATUS_SAVING : props.account.status}
          />
        </Modal>
      )}

      <PageHeader title={t("account_edit")}>
        <Button onClick={deleteAccount}>{t("account_delete")}</Button>
        <Button className="primary" onClick={getAccount}>
          {t("utils_connect")}
        </Button>
      </PageHeader>

      <h3>{t("account_informations")}</h3>

      <div className="flex">
        <TextInput
          id="name"
          onBlur={saveAccount}
          onChange={handleAccountChanges}
          status={props.account.status}
          title={t("account_name")}
          value={props.account.active.name}
        />
      </div>

      <div className="flex">
        <Dropdown
          list={BENCHMARK_SECTOR_LIST.map((id) => {
            return { id, name: t("benchmark_sector_" + id) }
          })}
          isNullAllowed
          onSelect={async (e) => {
            store.dispatch(accountStatus(STATUS_SAVING))
            const response = await store
              .dispatch(updateAccountSector_AsSupervisor({ accountId: props.account.active.id, sector: e.id }))
              .unwrap()
            if (!response?.error) handleAccountOptionsChanges("sector", e.id)
            store.dispatch(accountStatus(response?.error ? STATUS_SAVE_ERROR : STATUS_SAVED))
          }}
          displayField="name"
          status={props.account.status}
          title="Secteur"
          value={t("benchmark_sector_" + props.account.active.options.sector)}
        />

        {props._session.supervisorLevel > 1 && (
          <Dropdown
            active={getSupervisor(true)?.id}
            displayField="name"
            list={supervisors}
            isNullAllowed
            title={t("supervisor_sales")}
            onSave={saveAccount}
            onSelect={(supervisor) => selectSupervisor(supervisor, true)}
            status={props.account.status}
            value={getSupervisor(true)?.name}
          />
        )}

        {props._session.supervisorLevel > 1 && (
          <Dropdown
            active={getSupervisor(false)?.id}
            displayField="name"
            list={supervisors}
            isNullAllowed
            title={t("supervisor")}
            onSave={saveAccount}
            onSelect={(supervisor) => selectSupervisor(supervisor, false)}
            status={props.account.status}
            value={getSupervisor(false)?.name}
          />
        )}
      </div>

      <div
        className="flex"
        style={{
          marginTop: 20,
          marginBottom: 20
        }}>
        <Checkbox
          active={props.account.active.isTrial}
          id="isTrial"
          status={props.account.status}
          text={"Essai gratuit"}
          onClick={handleAccountCheckbox}
        />

        <Checkbox
          active={props.account.active.isSuperAccount}
          id="isSuperAccount"
          status={props.account.status}
          text={t("account_consulting")}
          onClick={handleAccountCheckbox}
        />

        {!props.account.active.isSuperAccount && (
          <div className="flex-auto" onClick={() => setCurrentModal(MODAL_CONVERT)}>
            <Link isWithoutMargin>{t("account_convert")}</Link>
          </div>
        )}

        <Space />
      </div>

      <div className="flex">
        <div>
          <div className="text-input-title grey-t" style={{ marginBottom: 12 }}>
            Type de contrat
          </div>
          <div className="flex" style={{ marginBottom: 32 }}>
            <AccountEditCsWidget currentAccount={props.account.active} />
          </div>
        </div>
        <div className="w-40"></div>
        {!props.account.active.isSuperAccount && (
          <div className="flex">
            <TextInput
              disabled
              id="superAccountId"
              onBlur={saveAccount}
              onChange={handleAccountChanges}
              status={props.account.status}
              title="Identifiant du compte parent"
              value={props.account.active.superAccountId}
            />
            <div className="mt-[54px] -ml-[80px]">
              <ListButton onClick={() => setCurrentModal(MODAL_SEARCH_PARENT)} icon={faSearch} text="Rechercher" />
            </div>
            <Space />
          </div>
        )}
      </div>

      <ModulesWidget
        features={props.account.active.features}
        modules={props.account.active.modules}
        onSave={() => saveAccount()}
      />

      <ListItem isSmall>
        <ListCell width={200}>
          <Checkbox
            active={props.account.active.options.sso}
            text={t("module_sso")}
            onClick={() => {
              handleAccountOptionsChanges("sso", !props.account.active.options.sso)
              setTimeout(() => {
                saveAccount()
              }, 250)
            }}
          />
          <Space />
        </ListCell>
        {props.account.active.options.sso && (
          <div>
            <TextInput
              id="ssoOrganization"
              onBlur={saveAccount}
              onChange={(e) => handleAccountOptionsChanges("ssoOrganization", e.value.toLowerCase())}
              title={t("account_sso_organization")}
              status={props.account.status}
              value={props.account.active.options.ssoOrganization}
            />
            <div className="h-10" />
            <Checkbox
              active={props.account.active.options.customEmailSenderInstance}
              text={t("admin_custom_email_sender_instance")}
              onClick={() => {
                handleAccountOptionsChanges(
                  "customEmailSenderInstance",
                  !props.account.active.options.customEmailSenderInstance
                )
                setTimeout(() => {
                  saveAccount()
                }, 250)
              }}
            />
          </div>
        )}
      </ListItem>

      <div className="height-20" />

      <div className="flex">
        <TextInput
          id="invitations"
          type="number"
          onBlur={saveAccount}
          onChange={handleAccountChanges}
          status={props.account.status}
          title={t("account_invitations")}
          value={props.account.active.invitations}
        />

        <TextInput
          id="participationMin"
          type="number"
          onBlur={saveAccount}
          onChange={handleAccountChanges}
          status={props.account.status}
          title={t("account_participation_min")}
          value={props.account.active.participationMin}
        />

        <TextInput
          id="ceilsTesters"
          type="number"
          onBlur={saveAccount}
          onChange={(e) => handleAccountOptionsChanges(e.id, parseInt(e.value))}
          status={props.account.status}
          title={t("account_participation_test")}
          value={props.account.active.options.ceilsTesters}
        />

        <Space />
      </div>

      <div className="height-20" />

      <ListItem>
        <div
          className="flex1 red-t"
          style={{
            fontWeight: "bold",
            textAlign: "right",
            margin: "auto",
            marginRight: 20,
            fontSize: 24
          }}>
          {t("generate_survey_demo")}
        </div>

        <BounceIn>
          <Button
            icon={faSkull}
            isLarge
            isLoading={isImportingDemoSurvey}
            className="warning"
            onClick={() => setCurrentModal(MODAL_IMPORT_DEMO_SURVEY)}>
            {t("GO")}
          </Button>
        </BounceIn>
      </ListItem>

      <hr></hr>

      <AccountSubscriptionsWidget />

      <div className="height-200" />
    </NavFocus>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  account: state.account
})

export default connect(mapStateToProps)(withTranslation()(SuperTrials))
